import React, { useRef, useEffect, useState, useMemo } from "react";
import { Box, IconButton, InputAdornment, Stack, TextField, Checkbox, FormControlLabel, Tooltip } from "@mui/material";
import { AgGridTable } from "components/tables/AgGridTable";
import { useDispatch, useSelector } from "react-redux";
import { assignProductsToCarousel, fetchCarouselIdProduct, openSnackbar } from "../../../redux/slices/common";
import { sortAndFilter } from "components/tables/AgGridTable";
import SearchIcon from "common/customIcons/search";
import { CloseRounded, IndeterminateCheckBox } from "@mui/icons-material";
import { useDebounce } from "hooks/useDebounce";
import MDButton from "components/MDButton";
import { useNavigate, useSearchParams } from "react-router-dom";

const ProductTable = ({ CarouselId, refreshKey, productLimit, products }) => {
  const dispatch = useDispatch();
  const mainGridApiRef = useRef("");
  const gridRefApi = useRef(null);
  const pageRef = useRef(0);
  const searchTermRef = useRef("");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedRows, setSelectedRows] = useState([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const [productsTotal, setProductsTotal] = useState(0);

  const { isgetLoading: isLoading = false, isAssignCarouselLoading = false } = useSelector((state) => state.common);
  const navigate = useNavigate()
  const fullRoleTab = useMemo(() => searchParams.get('platform'), [searchParams]);

  // Get min and max limits for isRowSelectable
  const minLimit = productLimit?.min ? Number(productLimit.min) : 0;
  const maxLimit = productLimit?.max ? Number(productLimit.max) : null;

  // Track selection state for indeterminate calculation
  const selectionState = useMemo(() => {
    if (!mainGridApiRef.current) return { isAllSelected: false, isIndeterminate: false };

    const totalVisibleRows = mainGridApiRef.current.getDisplayedRowCount ? mainGridApiRef.current.getDisplayedRowCount() : 0;
    const selectedCount = selectedRows.length;

    return {
      isAllSelected: totalVisibleRows > 0 && selectedCount === totalVisibleRows,
      isIndeterminate: selectedCount > 0 && selectedCount < totalVisibleRows,
      selectedCount,
      totalVisibleRows
    };
  }, [selectedRows, productsTotal]);

  const { isAllSelected, isIndeterminate } = selectionState;

  const handleSelectAllToggle = (event) => {
    if (!mainGridApiRef.current) return;

    const isChecked = event.target.checked;
    if (isChecked && maxLimit !== null && selectedRows.length >= maxLimit) {
      mainGridApiRef.current.deselectAll();
      setSelectedRows([]);
      return;
    }

    if (isChecked) {
      // Select all products
      const allRows = [];
      mainGridApiRef.current.forEachNode((node) => {
        if (node.data) {
          allRows.push(node);
        }
      });

      // If max limit is set, only select up to max limit
      if (maxLimit !== null && allRows.length > maxLimit) {
        // Select only up to max limit
        for (let i = 0; i < maxLimit && i < allRows.length; i++) {
          allRows[i].setSelected(true);
        }
        dispatch(openSnackbar({
          content: `Selected ${maxLimit} products (maximum limit)`,
          color: "info",
          icon: "info"
        }));
      } else {
        // Select all rows
        allRows.forEach(node => node.setSelected(true));
      }
    } else {
      // Deselect all products
      mainGridApiRef.current.deselectAll();
      setSelectedRows([]);
    }
  };

  // Custom header component for Select All checkbox
  const SelectAllHeaderComponent = useMemo(() => {
    return (params) => {
      const isDisabled = false; 
      // const isDisabled = maxLimit !== null && selectedRows.length >= maxLimit && !isAllSelected;

      return (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
          <Checkbox
            checked={isAllSelected}
            indeterminate={isIndeterminate}
            indeterminateIcon={<IndeterminateCheckBox color="primary" />}
            onChange={handleSelectAllToggle}
            disabled={isDisabled}
            size="small"
            sx={{
              padding: '4px',
              '&.Mui-disabled': {
                color: 'rgba(0, 0, 0, 0.26)'
              },
              '&.MuiCheckbox-indeterminate': {
                color: 'primary.main',
              }
            }}
          />
        </Box>
      );
    };
  }, [isAllSelected, isIndeterminate, selectedRows.length, maxLimit]);

  const columnDefs = useMemo(() => [
    {
      headerName: "",
      headerComponent: SelectAllHeaderComponent,
      checkboxSelection: true,
      suppressMenu: true,
      lockPosition: "left",
      pinned: "left",
      width: 50,
      minWidth: 50,
      maxWidth: 50,
      resizable: false,
      sortable: false,
      filter: false,
    },
    {
      headerName: "Product Name",
      field: "productName",
      suppressMenu: false,
      flex: 1,
      minWidth: 180,
    },

    {
      headerName: "Strain Type",
      field: "strainType",
      suppressMenu: false,
      flex: 1,
      minWidth: 130,
    },
    {
      headerName: "Product Brand",
      field: "brandName",
      suppressMenu: false,
      flex: 1,
      minWidth: 150,
    },
    {
      headerName: "Inventory",
      field: "totalQuantity",
      suppressMenu: false,
      flex: 1,
      minWidth: 80,
      cellRenderer: (props) => props.value || 0,
    },
    {
      headerName: "Price",
      field: "productPrice",
      suppressMenu: false,
      flex: 1,
      minWidth: 80,
      cellRenderer: (props) => {
        return props?.value ? <p>${props?.value} </p> : '';
      },
    },
    {
      headerName: "Traits",
      field: "productTraits",
      sortable: true,
      filter: false,
      flex: 1.5,
      minWidth: 200,
      menuTabs: [], // Remove column menu
      comparator: (valueA, valueB) => {
        // Sort by the first trait name if available
        const traitsA = Array.isArray(valueA) ? valueA.map(t => t?.subTraitName?.toLowerCase() || '').filter(Boolean) : [];
        const traitsB = Array.isArray(valueB) ? valueB.map(t => t?.subTraitName?.toLowerCase() || '').filter(Boolean) : [];
        const strA = traitsA.length > 0 ? traitsA[0] : '';
        const strB = traitsB.length > 0 ? traitsB[0] : '';
        return strA.localeCompare(strB);
      },
      cellRenderer: (params) => {
        const traits = params.value || [];
        if (!Array.isArray(traits) || traits.length === 0) return '-';
        return (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
            {traits.map((trait, idx) => (
              <span
                key={idx}
                style={{
                  background: '#f0f0f0',
                  padding: '2px 8px',
                  borderRadius: '4px',
                  fontSize: '12px',
                  whiteSpace: 'nowrap',
                  display: 'inline-block'
                }}
              >
                {trait?.subTraitName || ''}
              </span>
            ))}
          </div>
        );
      },
    },
  ], [isAllSelected, selectedRows.length, maxLimit, handleSelectAllToggle]);

  const getRows = async (params) => {
    if (!CarouselId) {
      setProductsTotal(0);
      params.successCallback([], 0);
      return;
    }

    gridRefApi.current = params;
    try {
      const term = (searchTermRef.current || "").trim().toLowerCase();

      // Only fetch from API if we don't have a search term or if we're on the first page
      const shouldFetchFromAPI = !term || pageRef.current === 0;
      let products = [];
      let totalCount = 0;

      if (shouldFetchFromAPI) {
        const res = await dispatch(
          fetchCarouselIdProduct({
            skip: pageRef.current,
            limit: 20,
            CarouselId,
            searchTerm: term // Pass the search term to the API if possible
          })
        ).unwrap();
        products = res?.data || [];
        totalCount = res?.total || 0;
      }

      // Process the products
      let filteredProducts = (products || [])
        .map((p) => ({ ...p, id: p?.id || p?.productId || p?._id }));

      // Apply client-side filtering only if there's a search term and we didn't filter on the server
      if (term && !shouldFetchFromAPI) {
        filteredProducts = filteredProducts.filter((p) => {
          const haystack = [
            p?.productName,
            p?.brandName,
            p?.strainType,
            Array.isArray(p?.productTraits)
              ? p.productTraits.map((t) => t?.subTraitName).filter(Boolean).join(" ")
              : "",
          ]
            .filter(Boolean)
            .join(" ")
            .toLowerCase();
          return haystack.includes(term);
        });
      }

      const dataAfterSortingAndFiltering = sortAndFilter(
        filteredProducts,
        params.sortModel,
        params.filterModel
      );

      // If we have a search term and no results, show empty state
      const hasSearchTerm = term && term.length > 0;
      const hasNoResults = dataAfterSortingAndFiltering.length === 0;

      // Only update total count if we're doing a search or if we have no results
      if (hasSearchTerm || hasNoResults) {
        totalCount = hasNoResults ? 0 : dataAfterSortingAndFiltering.length;
      }

      setProductsTotal(totalCount);
      params.successCallback(dataAfterSortingAndFiltering, totalCount);

      // Only increment page if we actually got results and we're not searching
      if (!hasSearchTerm && dataAfterSortingAndFiltering.length > 0) {
        pageRef.current = pageRef.current + 20;
      }
    } catch (error) {
      setProductsTotal(0);
      params.successCallback([], 0);
    }
  };

  const onSelectionChanged = () => {
    if (!mainGridApiRef.current) return;
    const rows = mainGridApiRef.current.getSelectedRows?.() || [];

    // Get min and max limits
    const minLimit = productLimit?.min ? Number(productLimit.min) : 0;
    const maxLimit = productLimit?.max ? Number(productLimit.max) : null;

    // If max limit is set and exceeded, deselect the last selected item
    if (maxLimit !== null && rows.length > maxLimit) {
      const selectedNodes = mainGridApiRef.current.getSelectedNodes?.() || [];
      // Find the last selected node that was just selected
      if (selectedNodes.length > maxLimit) {
        // Deselect the most recently selected item (last in the array)
        const lastSelectedNode = selectedNodes[selectedNodes.length - 1];
        if (lastSelectedNode) {
          lastSelectedNode.setSelected(false);
          dispatch(openSnackbar({
            content: `Maximum ${maxLimit} product${maxLimit > 1 ? 's' : ''} can be selected`,
            color: "warning",
            icon: "warning"
          }));
          // Don't update state here, let the deselection trigger another onSelectionChanged
          return;
        }
      }
    }

    // Update selected rows state
    setSelectedRows(rows);
  };

  const searchFn = () => {
    const dataSource = {
      getRows,
    };
    if (mainGridApiRef.current) {
      mainGridApiRef.current.ensureIndexVisible(0, null);
      mainGridApiRef.current.setGridOption("datasource", dataSource);
    }
  };

  useEffect(() => {
    if (mainGridApiRef?.current) {
      if (isLoading) {
        mainGridApiRef.current.showLoadingOverlay();
      } else if (productsTotal === 0) {
        mainGridApiRef.current.showNoRowsOverlay();
      } else {
        mainGridApiRef.current.hideOverlay();
      }
    }
  }, [isLoading, productsTotal]);

  useEffect(() => {
    if (CarouselId) {
      pageRef.current = 0;
      setProductsTotal(0);
      searchFn();
    } else {
      setProductsTotal(0);
    }
  }, [CarouselId]);

  // Refresh table when refreshKey changes (triggered after update)
  useEffect(() => {
    if (CarouselId && refreshKey !== undefined && refreshKey > 0) {
      pageRef.current = 0;
      setProductsTotal(0);
      searchFn();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshKey]);

  // Pre-select products based on products prop (when products prop changes)
  useEffect(() => {
    if (!mainGridApiRef.current || !products || !Array.isArray(products) || products.length === 0) {
      return;
    }

    // Extract product IDs from products array - handle both object and string formats
    const productIdsToSelect = products.map(p => {
      if (typeof p === 'string') return p;
      return p?.productId || p?.id || p?._id;
    }).filter(Boolean);

    if (productIdsToSelect.length === 0) return;

    // Wait for grid to be ready and data to load
    const timeoutId = setTimeout(() => {
      if (!mainGridApiRef.current) return;

      // First, clear existing selections
      mainGridApiRef.current.deselectAll();

      let selectedCount = 0;
      mainGridApiRef.current.forEachNode((node) => {
        if (node.data) {
          const productId = node.data?.productId || node.data?.id || node.data?._id;
          // Check if this product ID matches any in the products array
          if (productId && productIdsToSelect.some(id => String(id) === String(productId))) {
            // Respect max limit when pre-selecting
            if (maxLimit === null || selectedCount < maxLimit) {
              node.setSelected(true);
              selectedCount++;
            }
          }
        }
      });

      // Update selected rows state
      const selectedRows = mainGridApiRef.current.getSelectedRows?.() || [];
      setSelectedRows(selectedRows);
    }, 1000);

    return () => clearTimeout(timeoutId);
  }, [products, maxLimit]);

  const handleSearch = useDebounce(() => {
    pageRef.current = 0;
    setProductsTotal(0);
    searchFn();
  }, 500);

  const searchHandler = (searchValue) => {
    setSearchTerm(searchValue);
    searchTermRef.current = searchValue;
    handleSearch(searchValue);
  };

  const normalizeRowToAssignmentItem = (row) => ({
    productId: row?.productId || row?._id,
    productSlug: row?.productSlug || row?.slug,
    productName: row?.productName,
  });

  const handleClearSelection = () => {
    setSelectedRows([]);
    if (mainGridApiRef.current?.deselectAll) mainGridApiRef.current.deselectAll();
  };



  const handleAssign = async () => {
    const products = selectedRows.map(normalizeRowToAssignmentItem).filter((p) => p.productId && p.productName);

    if (!CarouselId) {
      dispatch(openSnackbar({ content: "CarouselId missing", color: "error", icon: "warning" }));
      return;
    }

    // Get min and max limits
    const minLimit = productLimit?.min ? Number(productLimit.min) : 0;
    const maxLimit = productLimit?.max ? Number(productLimit.max) : null;

    // Validate minimum limit
    if (minLimit > 0 && products.length < minLimit) {
      dispatch(openSnackbar({
        content: `Please select at least ${minLimit} product${minLimit > 1 ? 's' : ''}`,
        color: "error",
        icon: "warning"
      }));
      return;
    }

    // Validate maximum limit
    if (maxLimit !== null && products.length > maxLimit) {
      dispatch(openSnackbar({
        content: `Maximum ${maxLimit} products can be selected`,
        color: "error",
        icon: "warning"
      }));
      return;
    }

    if (!products.length) {
      dispatch(openSnackbar({ content: "Select at least 1 product", color: "error", icon: "warning" }));
      return;
    }

    try {
      const payload = { products };
      const res = await dispatch(assignProductsToCarousel({ carouselId: CarouselId, payload })).unwrap();
      dispatch(openSnackbar({ content: res?.message || "Products assigned successfully", color: "success", icon: "check" }));
      // handleClearSelection();
      navigate(`/hyperwolf/product-carousels?platform=${fullRoleTab || 'Hyperwolf'}`, { replace: true })
      // Refresh table to reflect assigned products if API changes listing.
      pageRef.current = 0;
      searchFn();
    } catch (e) {
      const msg = e?.message || e?.data?.message || "Failed to assign products";
      dispatch(openSnackbar({ content: msg, color: "error", icon: "warning" }));
    }
  };

  return (
    <Box mt={2}>
      <Stack direction="row" gap={2} justifyContent="space-between" alignItems="center" flexWrap="wrap" mb={2}>
        <TextField
          className="custom-search-field remove-white"
          placeholder="Search"
          label=""
          value={searchTerm}
          onChange={(e) => searchHandler(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <IconButton size="small">
                  <SearchIcon width="18px" height="18px" />
                </IconButton>
              </InputAdornment>
            ),
            endAdornment:
              searchTerm?.length > 0 ? (
                <InputAdornment>
                  <IconButton onClick={() => searchHandler("")} size="small">
                    <CloseRounded />
                  </IconButton>
                </InputAdornment>
              ) : null,
          }}
          style={{ minWidth: "300px" }}
        />

        <Stack direction="row" gap={1} flexWrap="wrap">
          <Box sx={{ display: "flex", alignItems: "center", color: "#9A9AAF", fontWeight: 600, fontSize: 14, px: 1 }}>
            {selectedRows.length} products selected
            {productLimit?.min || productLimit?.max ? (
              <span style={{ marginLeft: 8, color: "#707070" }}>
                ({productLimit?.min ? `Min: ${productLimit.min}` : ''}
                {productLimit?.min && productLimit?.max ? ' | ' : ''}
                {productLimit?.max ? `Max: ${productLimit.max}` : ''})
              </span>
            ) : null}
          </Box>

          <Tooltip
            title={
              (!CarouselId || !selectedRows.length) ? '' :
                (minLimit > 0 && selectedRows.length < minLimit) ? 'Selected items are less than the minimum required' :
                  (maxLimit !== null && selectedRows.length > maxLimit) ? 'Selected items exceed the maximum allowed limit' :
                    ''
            }
            disableHoverListener={
              !(!CarouselId || !selectedRows.length ||
                (minLimit > 0 && selectedRows.length < minLimit) ||
                (maxLimit !== null && selectedRows.length > maxLimit))
            }
            placement="top"
            arrow
          >
            <Box>
              <MDButton
                variant="contained"
                color="primary"
                onClick={handleAssign}
                loading={isAssignCarouselLoading}
                disabled={
                  !CarouselId ||
                  !selectedRows.length ||
                  (minLimit > 0 && selectedRows.length < minLimit) ||
                  (maxLimit !== null && selectedRows.length > maxLimit)
                }
                style={{ minWidth: "185px" }}
              >
                Assign
              </MDButton>
            </Box>
          </Tooltip>
        </Stack>
      </Stack>
      <div className="last-child-left">
        <AgGridTable
          col={columnDefs}
          getRows={getRows}
          mainGridApiRef={mainGridApiRef}
          pageRef={pageRef}
          isLoading={isLoading}
          onSelectionChanged={onSelectionChanged}
          gridOptions={{}}
          style={{ height: "60vh" }}
        />
      </div>
    </Box>
  );
};

export default ProductTable;